import { Button, CircularProgress, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { CARD_EXPIRY_STATUS, EXPIRY_STATUS, MODALS } from "../../../config/params";
import React, { useEffect, useState } from "react";

import AddPaymentIcon from "../../../components/Icons/AddPaymentIcon";
import { CentralLoadingIndicator } from "../../../components";
import { ChipCustom } from "../../../styles/styledComponents";
import { CloseIcon } from "../../../components/Icons";
import { ICardItemResponse } from "../../../services/card-list/card-list.interface";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { PAGES_PATH } from "../../../navigation/params";
import { providerIcon } from "../../../components/CardProviderDetails/CardProviderDetails";
import { useCardDetailStore } from "../../../services/card-detail/card-detail.service";
import { useCardList } from "../../../services/card-list/card-list.service";
import { useModal } from "../../../context/ModalContext";
import { usePetStore } from "../../../hooks/redux-hooks/pet-store/pet-store.service";
import { useSearchParams } from "react-router-dom";
import { useUpdateSubscriptionPaymentMethod } from
  "../../../services/update-Subscription-PaymentMethod/update-Subscription-PaymentMethod.service";
import { useWellnessPlanRecord } from "../../../hooks";
import { userActivityLogger } from "../../../utils/mixpanel";

const UpdatePaymentMethodModal = () => {
  const { cardList, loading } = useCardList();
  const { closeModal, openModal } = useModal();
  // eslint-disable-next-line no-unused-vars
  const [defaultCard, setDefaultCard] = useState<ICardItemResponse | undefined>();
  const [petName, setPetName] = useState<string>("");
  const { wellnessPlanRecord: { wellnessPlanDetails } } = useWellnessPlanRecord();
  const [searchParams] = useSearchParams();
  const petId = searchParams.get("id");
  const { petList } = usePetStore();
  const { cardSelected, setCardSelected } = useCardDetailStore();
  const [value, setValue] = useState<string | undefined>("");
  const formattedExpMonth =(expMonth: number)=> String(expMonth).padStart(2, "0");

  const [isDefaultCardChanged, setIsDefaultCardChanged] =
   useState<string | undefined>("");

  const { updateSubscriptionPayment, loading: updateLoading, response } = useUpdateSubscriptionPaymentMethod();


  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, card: ICardItemResponse) => {
    setDefaultCard(card);
    setIsDefaultCardChanged(card.id);
    setValue(card.id);
  };

  useEffect(() => {
    const cards = cardList?.cards || [];

    if (cards.length > 0) {
      const subscriptionDefaultCard = cards.find(
        (card) => card.id === wellnessPlanDetails?.cardId,
      );

      if (subscriptionDefaultCard) {
        setIsDefaultCardChanged(subscriptionDefaultCard?.id);
        setDefaultCard(subscriptionDefaultCard);
        setValue(subscriptionDefaultCard?.id);
      } else {
        if (cardSelected) {
          setValue(cardSelected?.id);
          setDefaultCard(cardSelected);
        } else {
          if (cardList && cards.length === 1) {
            setIsDefaultCardChanged(cards[0].id);
            setDefaultCard(cards[0]);
            setValue(cards[0].id);
            setCardSelected(cards[0]);
          }
        }
      }
    }
  }, [cardList]);

  useEffect(() => {
    if (petList && petList.length > 0) {
      const currentPet = petList.filter((pet) => pet.id == petId);

      setPetName(currentPet[0]?.name || "");
    }
  }, [petList]);

  const handleCloseModal = () => {
    closeModal();
  };

  const handleUpdateMethod =()=>{
    if (window.location.href.includes(PAGES_PATH.WELLNESS_PLAN_CONFIRM_AND_PAY) && defaultCard) {
      setCardSelected(defaultCard);
      closeModal();
    } else {
      if (wellnessPlanDetails?.wellnessId && isDefaultCardChanged) {
        userActivityLogger(MIXPANEL_EVENTS.USER_UPDATE_PAYMENT_METHOD_TRIGGERED,
          {
            pet_name: petName,
          });
        updateSubscriptionPayment((wellnessPlanDetails?.wellnessId!)?.toString(), isDefaultCardChanged);
      }
    }
  };

  const handleAddNewPaymentMethod = () => {
    /* navigate(PAGES_PATH.USER_PROFILE+"/"
     +PAGES_PATH.USER_BILLING+"/"+PAGES_PATH.BILLING_METHOD); */
    openModal(MODALS.ADD_PAYMENT_METHOD);
  };

  const renderCardStatusTag = (card: ICardItemResponse) => {
    if (!card) {
      return null;
    }
    if (card.isActive === false || card.expiryStatus?.toLowerCase() === EXPIRY_STATUS.DISABLED) {
      return <ChipCustom
        label={CARD_EXPIRY_STATUS.DISABLED}
        className="!font-semibold !text-xs !py-0.5 !rounded-full"
        color={"default"}
        size="small"
      />;
    }
    const isCardExpiringSoon = card.expiryStatus?.toLowerCase() === EXPIRY_STATUS.EXPIRING_SOON;

    if (isCardExpiringSoon) {
      return <ChipCustom
        label={CARD_EXPIRY_STATUS.EXPIRING_SOON}
        className="!text-xs !py-0.5 !rounded-full"
        color={"warning"}
        size="small"
      />;
    }
    const isCardExpired = card.expiryStatus?.toLowerCase() === EXPIRY_STATUS.EXPIRED;

    if (isCardExpired) {
      return <ChipCustom
        label={CARD_EXPIRY_STATUS.EXPIRED}
        className="!text-xs !py-0.5 !rounded-full"
        color={"error"}
        size="small"
      />;
    }

    const isCardIssuerReportAlert = card.expiryStatus?.toLowerCase() === EXPIRY_STATUS.ISSUER_REPORT;

    if (isCardIssuerReportAlert) {
      return <ChipCustom
        label={CARD_EXPIRY_STATUS.ISSUER_REPORT}
        className="!text-xs !py-0.5 !rounded-full"
        color={"error"}
        size="small"
      />;
    }

    return null;
  };

  useEffect(()=>{
    if (!response) return;

    if (response.success) {
      userActivityLogger(MIXPANEL_EVENTS.USER_UPDATE_PAYMENT_METHOD_SUCCESS,
        {
          pet_name: petName,
        });
      closeModal();
    } else {
      userActivityLogger(MIXPANEL_EVENTS.USER_UPDATE_PAYMENT_METHOD_FAILURE,
        {
          pet_name: petName,
          error_message: response.messages[0],
        });
    }
  }, [response]);

  if (loading) {
    return <CentralLoadingIndicator />;
  }

  return (
    <div className="w-full px-1 sm:px-4 py-1 !h-full flex flex-col">
      <div className="flex-1 flex flex-col min-h-0 overflow-y-auto gap-3">
        <div className="flex w-full justify-end">
          <CloseIcon
            onClick={handleCloseModal}
            className="text-black cursor-pointer mt-1"
          />
        </div>
        <div className="flex flex-col gap-4 items-center self-stretch flex-1">
          <div className="text-2xl font-semibold text-center geograph-regular">
             Payment Method
          </div>
          <div className="w-full flex flex-col gap-3">
            {cardList && cardList.cards.length > 0 ? cardList?.cards?.map((card)=>(
              <div className="border border-taupe-50 rounded-lg bg-white p-3
              flex justify-between"
              key={card.id}>
                {/* Card Info part */}
                <div className="min-w-0">
                  <div className="flex gap-2 items-center min-w-0">
                    <div className="flex-shrink-0">{providerIcon(card.cardBrand)}</div>
                    <div className="text-sm font-semibold flex flex-wrap items-center capitalize gap-x-1">
                      <span>{card.cardBrand}</span>
                      <span className="relative top-1">**** **** **** {card.last4}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 ms-1 mt-1 text-xs">
                    <span className=" text-gray-1">Expiry date</span>
                    <span> {formattedExpMonth(card.expMonth)}/{card.expYear} </span>
                  </div>
                  <div className="sm:hidden flex flex-col gap-1 w-4/6 mt-2">
                    {((card.isDefault)) && <ChipCustom
                      label={"Default"}
                      className="!text-xs !py-[0.375rem] !px-2 !rounded-full !text-mustard"
                      color={"primary"}
                      size="small"
                    />}
                    {renderCardStatusTag(card)}
                  </div>
                </div>
                <div className="flex gap-1 items-center justify-end">
                  {/* Default Chip component */}
                  <div className="hidden sm:flex flex-col gap-1">
                    {((card.isDefault)) && <ChipCustom
                      label={"Default"}
                      className="!text-xs !py-[0.375rem] !px-2 !rounded-full !text-mustard"
                      color={"primary"}
                      size="small"
                    />}
                    {renderCardStatusTag(card)}
                  </div>

                  <RadioGroup
                    aria-labelledby="demo-radio-buttons-group-label"
                    name="radio-buttons-group"
                    value={value}
                    onChange={(e)=>handleChange(e, card)}
                    sx={{ "& .MuiFormControlLabel-root": {
                      margin: "0",
                    } }}
                  >
                    <FormControlLabel
                      value={card.id}
                      control={<Radio color="primary" sx={{ "&.Mui-checked": { color: "primary.main" } }} />}
                      label=""
                    />
                  </RadioGroup>


                </div>
              </div>
            )) : null}
          </div>
          {/* ADD PAYMENT METHOD */}
          <div className="w-full">
            <Button
              type="button"
              variant="outlined"
              fullWidth
              className="!text-sm !normal-case flex gap-1 items-center !font-semibold"
              onClick={handleAddNewPaymentMethod}>
              <AddPaymentIcon width={24} height={24} />
              Add Payment Method
            </Button>
          </div>
        </div>
      </div>
      <div className="pt-3 pb-1 flex-shrink-0">
        <Button variant="contained" color="primary" sx={{ fontWeight: "bold" }}
          fullWidth onClick={handleUpdateMethod}
          disabled={updateLoading}>

          {updateLoading ?
            <CircularProgress color="inherit" size={20} />:
            "Update Payment Method"}
        </Button>
      </div>
    </div>
  );
};

export default UpdatePaymentMethodModal;
